import React, { useState } from "react";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import ReportIssueDialog from "./ReportIssueDialog";

/**
 * ReportIssueButton: the entry point that turns "something is wrong with this
 * order" into a support case the customer can then follow.
 *
 * Self contained on purpose. It carries its own button, its own dialog and its
 * own navigation, so an order screen adds the whole flow with one line and
 * nothing else on that screen changes shape.
 *
 * `order` is the raw order document. The vendor and order ids are pulled out
 * of it inside the dialog by orderContextForCase, so a caller never has to
 * know which of the order shapes carries the vendor.
 *
 * On success the customer lands on the new case, because a report that
 * disappears into a toast is indistinguishable from one that was not sent.
 */
const ReportIssueButton = ({ order, label = "Report an issue" }) => {
  const history = useHistory();
  const [open, setOpen] = useState(false);

  const handleCreated = (created) => {
    if (created?._id) history.push(`/support-cases/${created._id}`);
    else history.push("/support-cases");
  };

  return (
    <Wrap>
      <Row>
        <Trigger type="button" onClick={() => setOpen(true)}>
          <FlagOutlinedIcon fontSize="small" />
          {label}
        </Trigger>
        <Link type="button" onClick={() => history.push("/support-cases")}>
          My reported issues
        </Link>
      </Row>
      <Note>
        Wrong item, missing item, delivery or payment: tell us here and follow
        the answer in one place.
      </Note>
      <ReportIssueDialog
        open={open}
        order={order}
        onClose={() => setOpen(false)}
        onCreated={handleCreated}
      />
    </Wrap>
  );
};

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
`;

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #d8d8d5;
  border-radius: 6px;
  background: #ffffff;
  color: #1f1f1f;
  padding: 10px 18px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f3f2;
    border-color: #b9b9b5;
  }
`;

const Link = styled.button`
  border: none;
  background: transparent;
  color: #4b5563;
  font-size: 0.8125rem;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
  padding: 4px;
`;

const Note = styled.div`
  font-size: 12px;
  color: #6e6e6e;
  text-align: center;
  max-width: 420px;
  line-height: 1.45;
`;

export default ReportIssueButton;
